import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  getCheckingAccountsSeenStatus,
  getHomeDashboard,
  MarkAsSeenRequest,
  markCheckingAccountsAsSeen,
} from '~apis/home';

export const HomeQueryKey = {
  default: () => ['home'],
  dashboard: ({
    companyId,
    startDate,
    endDate,
  }: {
    companyId: string;
    startDate?: string;
    endDate?: string;
  }) => [...HomeQueryKey.default(), 'dashboard', companyId, startDate, endDate],
  checkingAccountsSeenStatus: ({ companyId }: { companyId: string }) => [
    ...HomeQueryKey.default(),
    'checkingAccountsSeenStatus',
    companyId,
  ],
};

export const useGetHomeDashboard = ({
  companyId,
  startDate,
  endDate,
  options,
}: {
  companyId: string;
  startDate?: string;
  endDate?: string;
  options?: { keepPreviousData?: boolean };
}) => {
  let validEndDate = endDate;
  if (endDate && dayjs(endDate).isAfter(dayjs())) {
    validEndDate = dayjs().format('YYYY-MM-DD');
  }

  return useQuery({
    queryKey: HomeQueryKey.dashboard({ companyId, startDate, endDate: validEndDate }),
    queryFn: () => getHomeDashboard({ companyId, startDate, endDate: validEndDate }),
    enabled: !!companyId,
    keepPreviousData: options?.keepPreviousData ?? true,
  });
};

export const useGetCheckingAccountsSeenStatus = ({
  companyId,
  enabled = true,
}: {
  companyId: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: HomeQueryKey.checkingAccountsSeenStatus({ companyId }),
    queryFn: () => getCheckingAccountsSeenStatus({ companyId }),
    enabled: !!companyId && enabled,
  });
};

export const useMarkCheckingAccountsAsSeen = ({ companyId }: { companyId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: MarkAsSeenRequest) => markCheckingAccountsAsSeen({ companyId, request }),
    onSuccess: () => {
      queryClient.invalidateQueries(HomeQueryKey.checkingAccountsSeenStatus({ companyId }));
    },
  });
};
